import { RefObject, useCallback, useEffect } from "react";

interface IClickOutside {
  /** Referencia do container que, quando clicado fora dele, vai executar a funcao passada no onClickOutside */
  ref: RefObject<HTMLElement>;
  /** Funcao que vai ser executada quando o clique acontecer fora do container de referencia */
  onClickOutside: (event?: MouseEvent) => void;
  /** Um array de strings com nomes de classes que, quando estiverem renderizadas em tela, nao acionarao a funcao de onClickOutside, essa prop tem utilidade quando elementos como toasts ou outros modais estao renderizados em tela, pra nao fechar o modal aberto ao clicar nesses elementos */
  avoidRenderedClasses?: string[];
  /** Um array de strings com nomes de classes que, quando o elemento clicado conter uma das classes, nao acionara a funcao de onClickOutside, essa prop tem utilidade quando elementos como toasts ou outros modais estao renderizados em tela, pra nao fechar o modal aberto ao clicar nesses elementos */
  avoidElementHaveClasses?: string[];
}

/**
 * @hook {useClickOutside}
 * Esse hook faz com que uma funcao seja executada quando um click ocorre fora do container de referencia.
 * Util para fechar modais, menus, toasts, etc.
 *
 * @example
 *  ```jsx
 *    const modalContainerRef = useRef(null)
 *
 *    useClickOutside({
 *        ref: modalContainerRef,
 *        onClickOutside: closeModal,
 *        avoidClasses: ['toastClass', 'alertClass']
 *    })
 *
 *    <ModalContainer ref={modalContainerRef} />
 *  ```
 *
 */
function useClickOutside({
  ref,
  onClickOutside,
  avoidElementHaveClasses,
  avoidRenderedClasses,
}: IClickOutside) {
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      // Not trigger case when the element clicked have the class to be avoid
      if (avoidElementHaveClasses) {
        const target = event.target as HTMLElement;
        const targetClassList = target.classList;

        let triggerClickOutside = true;

        Array.from(targetClassList).forEach((className) => {
          if (avoidElementHaveClasses?.includes(className)) {
            triggerClickOutside = false;
          }
        });

        if (!triggerClickOutside) return;
      }

      // Not trigger case when the element that have the class to be avoid is on screen
      let isSomeElementVisible;

      if (avoidRenderedClasses) {
        isSomeElementVisible = avoidRenderedClasses
          .map((className) => {
            const element = document.querySelector(`.${className}`);
            return element;
          })
          .some((element) => element);
      }

      if (isSomeElementVisible) return;

      if (ref.current && !ref.current.contains(event.target as HTMLElement)) {
        onClickOutside(event);
      }
    },
    [ref, onClickOutside, avoidElementHaveClasses, avoidRenderedClasses]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);
}

export default useClickOutside;
